/* Anjaly - shared front-end helpers (slider, media videos, coupon copy, sound toggle). */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const isRtl = () => document.documentElement.dir === 'rtl';

  const isShown = (el) => el.offsetParent !== null || getComputedStyle(el).position === 'fixed';

  /* Play only the videos that are visible at the current breakpoint and inside an active slide. */
  const syncVideos = (root = document) => {
    root.querySelectorAll('.a-media__video').forEach((video) => {
      const slide = video.closest('.a-slide');
      const inactive = slide && slide.dataset.active === 'false';
      if (!isShown(video) || inactive || video.dataset.offscreen === 'true') {
        video.pause();
      } else if (video.paused && !reduceMotion.matches) {
        video.play().catch(() => {});
      }
    });
  };

  const videoObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      entry.target.dataset.offscreen = entry.isIntersecting ? 'false' : 'true';
    });
    syncVideos();
  });

  const watchVideos = (root = document) => {
    root.querySelectorAll('.a-media__video').forEach((video) => {
      if (video.dataset.watched) return;
      video.dataset.watched = 'true';
      if (reduceMotion.matches) video.removeAttribute('autoplay');
      videoObserver.observe(video);
    });
    syncVideos(root);
  };

  class AnjalySlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector(':scope > .a-slider__track');
      if (!this.track || this.initialized) return;
      this.initialized = true;
      this.slides = Array.from(this.track.children);
      this.index = 0;
      this.slides.forEach((slide, i) => { slide.dataset.active = i === 0 ? 'true' : 'false'; });
      if (this.slides.length < 2) return;

      this.buildControls();

      this.slideObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            this.setActive(this.slides.indexOf(entry.target));
          }
        });
      }, { root: this.track, threshold: [0.6] });
      this.slides.forEach((slide) => this.slideObserver.observe(slide));

      this.interval = parseInt(this.dataset.interval || '5000', 10);
      this.autoplay = this.dataset.autoplay === 'true' && !reduceMotion.matches;

      this.viewObserver = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        this.inView ? this.start() : this.stop();
      });
      this.viewObserver.observe(this);

      ['mouseenter', 'focusin'].forEach((ev) => this.addEventListener(ev, () => this.stop()));
      ['mouseleave', 'focusout'].forEach((ev) => this.addEventListener(ev, () => this.start()));
      this.track.addEventListener('pointerdown', () => { this.autoplay = false; this.stop(); }, { passive: true });
    }

    disconnectedCallback() {
      this.stop();
      this.slideObserver?.disconnect();
      this.viewObserver?.disconnect();
    }

    buildControls() {
      const label = (key, fallback) => this.dataset[key] || fallback;

      if (this.dataset.arrows !== 'false') {
        const chevron = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M10 3 5 8l5 5" stroke="currentColor" stroke-width="1.5"/></svg>';
        [['prev', label('labelPrev', 'הקודם'), -1], ['next', label('labelNext', 'הבא'), 1]].forEach(([dir, text, step]) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = `a-slider__arrow a-slider__arrow--${dir}`;
          button.setAttribute('aria-label', text);
          button.innerHTML = chevron;
          button.addEventListener('click', () => { this.autoplay = false; this.goTo(this.index + step); });
          this.appendChild(button);
        });
      }

      if (this.dataset.dots !== 'false') {
        this.dots = document.createElement('div');
        this.dots.className = 'a-slider__dots';
        this.slides.forEach((_, i) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'a-slider__dot';
          dot.setAttribute('aria-label', `${label('labelSlide', 'שקופית')} ${i + 1}`);
          dot.setAttribute('aria-current', i === 0 ? 'true' : 'false');
          dot.addEventListener('click', () => { this.autoplay = false; this.goTo(i); });
          this.dots.appendChild(dot);
        });
        this.appendChild(this.dots);
      }
    }

    setActive(i) {
      if (i < 0) return;
      this.index = i;
      this.slides.forEach((slide, n) => { slide.dataset.active = n === i ? 'true' : 'false'; });
      this.dots?.querySelectorAll('.a-slider__dot').forEach((dot, n) => dot.setAttribute('aria-current', n === i ? 'true' : 'false'));
      syncVideos(this);
    }

    goTo(i) {
      const count = this.slides.length;
      const target = this.slides[((i % count) + count) % count];
      const trackRect = this.track.getBoundingClientRect();
      const slideRect = target.getBoundingClientRect();
      const delta = isRtl() ? slideRect.right - trackRect.right : slideRect.left - trackRect.left;
      this.track.scrollBy({ left: delta, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }

    start() {
      this.stop();
      if (!this.autoplay || !this.inView) return;
      this.timer = setInterval(() => this.goTo(this.index + 1), this.interval);
    }

    stop() {
      clearInterval(this.timer);
    }
  }

  if (!customElements.get('anjaly-slider')) customElements.define('anjaly-slider', AnjalySlider);

  document.addEventListener('click', (event) => {
    const copy = event.target.closest('[data-a-copy]');
    if (copy && copy.dataset.aCopyToast) {
      event.preventDefault();
      event.stopPropagation();
      const code = copy.dataset.aCopy;
      const fallback = () => {
        const field = document.createElement('textarea');
        field.value = code; field.setAttribute('readonly', ''); field.style.cssText = 'position:fixed;opacity:0;pointer-events:none';
        document.body.appendChild(field); field.select();
        try { document.execCommand('copy'); } catch (error) { /* nothing else to try */ }
        field.remove();
      };
      const write = navigator.clipboard ? navigator.clipboard.writeText(code) : Promise.reject();
      Promise.race([write.catch(fallback), new Promise((r) => setTimeout(r, 800))]).finally(() => {
        copy.classList.add('is-copied');
        setTimeout(() => copy.classList.remove('is-copied'), 2000);
        let toast = document.querySelector('.a-copy-toast');
        if (!toast) { toast = document.createElement('div'); toast.className = 'a-added-toast a-copy-toast'; toast.setAttribute('role', 'status'); document.body.appendChild(toast); }
        toast.textContent = copy.dataset.aCopyToast;
        requestAnimationFrame(() => toast.classList.add('is-visible'));
        clearTimeout(toast._t); toast._t = setTimeout(() => toast.classList.remove('is-visible'), 2000);
      });
      return;
    }
    if (copy) {
      const code = copy.dataset.aCopy;
      const done = copy.querySelector('[data-a-copy-status]');
      const legacyCopy = () => {
        const field = document.createElement('textarea');
        field.value = code;
        field.setAttribute('readonly', '');
        field.style.cssText = 'position:fixed;opacity:0;pointer-events:none';
        document.body.appendChild(field);
        field.select();
        try { document.execCommand('copy'); } catch (error) { /* nothing else to try */ }
        field.remove();
      };
      const confirmCopied = () => {
        if (!done || copy.classList.contains('is-copied')) return;
        const original = done.textContent;
        done.textContent = copy.dataset.aCopied || 'COPIED!';
        copy.classList.add('is-copied');
        setTimeout(() => { done.textContent = original; copy.classList.remove('is-copied'); }, 2000);
      };
      const write = navigator.clipboard ? navigator.clipboard.writeText(code) : Promise.reject();
      const timeout = new Promise((resolve) => setTimeout(resolve, 800));
      Promise.race([write.catch(legacyCopy), timeout]).finally(confirmCopied);
    }

    const sound = event.target.closest('[data-a-sound]');
    if (sound) {
      const scope = sound.closest('.a-slide') || sound.parentElement;
      const soundOn = sound.getAttribute('aria-pressed') !== 'true';
      scope.querySelectorAll('.a-media__video').forEach((video) => { video.muted = !soundOn; });
      sound.setAttribute('aria-pressed', soundOn ? 'true' : 'false');
    }
  });

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => syncVideos(), 200);
  });

  const init = () => watchVideos();
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', init) : init();
  document.addEventListener('shopify:section:load', (event) => watchVideos(event.target));
})();

/* Size chart: hide the trigger when the chart is turned off in the editor */
(() => {
  const sync = () => {
    document.querySelectorAll('.a-size-chart-trigger').forEach((button) => { button.hidden = !document.getElementById(button.getAttribute('aria-controls')); });
  };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', sync) : sync();
  let queued = false;
  new MutationObserver(() => { if (queued) return; queued = true; requestAnimationFrame(() => { queued = false; sync(); }); }).observe(document.documentElement, { childList: true, subtree: true });
})();

/* Collection grid: place banners after row N and fill the last row (by the current number of columns) */
(() => {
  const layout = (list) => {
    const columns = getComputedStyle(list).gridTemplateColumns.split(' ').filter(Boolean).length || 1;
    const layoutKey = columns + '|' + window.location.search;
    if (list.dataset.anjalyColumns === layoutKey) return;
    list.dataset.anjalyColumns = layoutKey;

    list.querySelectorAll('.a-filler').forEach((filler) => filler.remove());
    const cards = () => Array.from(list.children).filter((child) => child.matches('product-card, .product-card, .collection-promo-block') && !child.classList.contains('a-filler'));

    list.querySelectorAll('.a-grid-banner').forEach((banner) => {
      const index = parseInt(banner.dataset.afterRow || '2', 10) * columns;
      const target = cards()[index];
      target ? list.insertBefore(banner, target) : list.appendChild(banner);
      banner.classList.add('is-placed');
    });

    const templates = Array.from(list.querySelectorAll('template.a-filler-template'));
    const filtered = Array.from(new URLSearchParams(window.location.search).keys()).some((key) => key.startsWith('filter.'));
    if (filtered) return;
    const remainder = cards().length % columns;
    if (!templates.length || remainder === 0) return;
    for (let i = 0; i < columns - remainder; i++) {
      const node = templates[i % templates.length].content.firstElementChild.cloneNode(true);
      node.classList.add('a-filler');
      list.appendChild(node);
    }
  };

  const run = () => document.querySelectorAll('product-list').forEach((list) => {
    if (list.querySelector('.a-grid-banner, template.a-filler-template')) layout(list);
  });

  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', run) : run();
  let timer;
  window.addEventListener('resize', () => { clearTimeout(timer); timer = setTimeout(run, 150); });
  document.addEventListener('shopify:section:load', run);
  new MutationObserver((mutations) => {
    if (mutations.some((m) => Array.from(m.addedNodes).some((n) => n.nodeType === 1 && (n.matches?.('product-list') || n.querySelector?.('product-list'))))) {
      document.querySelectorAll('product-list').forEach((list) => delete list.dataset.anjalyColumns);
      run();
    }
  }).observe(document.body, { childList: true, subtree: true });
})();

/* Live filtering inside the open filter drawer: update the products, the count and the drawer content
   without replacing the drawer (the theme's default re-render closes it). */
(() => {
  let controller = null;
  document.addEventListener('facet:update', async (event) => {
    const drawer = document.querySelector('#facets-drawer[open]');
    if (!drawer) return;
    event.stopImmediatePropagation();
    if (controller) controller.abort();
    controller = new AbortController();

    const url = new URL(event.detail.url);
    const cleanUrl = new URL(url);
    cleanUrl.searchParams.delete('section_id');
    history.replaceState({}, '', cleanUrl.toString());

    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
    try {
      const html = await (await fetch(url.toString(), { signal: controller.signal })).text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const swap = (selector, root = document) => {
        const current = root.querySelector(selector);
        const fresh = doc.querySelector(selector);
        if (current && fresh) current.replaceWith(document.importNode(fresh, true));
      };
      swap('.collection__main');
      swap('.collection-toolbar__products-count');
      swap('.collection-toolbar');

      const freshForm = doc.querySelector('#facets-drawer facets-form');
      const currentForm = drawer.querySelector('facets-form');
      if (freshForm && currentForm) currentForm.innerHTML = freshForm.innerHTML;
      const freshApply = doc.querySelector('#facets-drawer .a-facets-drawer__apply');
      const currentApply = drawer.querySelector('.a-facets-drawer__apply');
      if (freshApply && currentApply) currentApply.textContent = freshApply.textContent;
      const freshClear = doc.querySelector('#facets-drawer .a-facets-drawer__clear');
      const currentClear = drawer.querySelector('.a-facets-drawer__clear');
      if (freshClear && currentClear) currentClear.setAttribute('href', freshClear.getAttribute('href'));
    } catch (error) {
      if (error.name !== 'AbortError') window.location.href = cleanUrl.toString();
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }, true);
})();

/* Tag chips built from the collection tags: reflect the active tags from the URL */
(() => {
  const sync = () => {
    const active = new URLSearchParams(window.location.search).getAll('filter.p.tag');
    document.querySelectorAll('input[name="filter.p.tag"]').forEach((input) => {
      if (active.includes(input.value)) input.checked = true;
    });
  };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', sync) : sync();
  let queued = false;
  new MutationObserver(() => { if (queued) return; queued = true; requestAnimationFrame(() => { queued = false; sync(); }); }).observe(document.documentElement, { childList: true, subtree: true });
})();

/* Product counts follow the cards actually shown (a product split into colors shows several cards).
   On collections with more than one page the Shopify product count is kept. */
(() => {
  const update = () => {
    const list = document.querySelector('product-list');
    if (!list) return;
    if (document.querySelector('.pagination [rel="next"], .pagination [rel="prev"]')) return;
    const cards = Array.from(list.children).filter((child) => child.matches('product-card') && !child.classList.contains('a-filler')).length;
    document.querySelectorAll('.collection-toolbar__products-count, .a-facets-drawer__apply').forEach((element) => {
      const next = element.textContent.replace(/\d+/, String(cards));
      if (next !== element.textContent) element.textContent = next;
    });
  };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', update) : update();
  let timer;
  new MutationObserver(() => { clearTimeout(timer); timer = setTimeout(update, 50); })
    .observe(document.documentElement, { childList: true, subtree: true });
})();

/* Product page: "Buy now" goes through the Shopify cart, then to checkout */
document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-anjaly-buy-now]');
  if (!button) return;
  event.preventDefault();
  const form = document.getElementById(button.closest('buy-buttons')?.getAttribute('form')) || button.closest('form') || button.closest('product-form')?.querySelector('form');
  if (!form) return;
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  try {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form),
    });
    if (!response.ok) throw new Error('add failed');
    window.location.href = `${window.Shopify?.routes?.root || '/'}checkout`;
  } catch (error) {
    button.disabled = false;
    button.removeAttribute('aria-busy');
    alert('לא ניתן לעבור לתשלום כרגע, נסי שוב');
  }
});

/* Product gallery: mouse wheel moves between images (desktop), like the live site */
(() => {
  let last = 0;
  document.addEventListener('wheel', (event) => {
    const media = event.target.closest('.product-gallery__carousel, .product-gallery__media-list');
    if (!media || window.matchMedia('(max-width: 767px)').matches) return;
    const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
    if (Math.abs(delta) < 10) return;
    event.preventDefault();
    const now = Date.now();
    if (now - last < 350) return;
    last = now;
    const gallery = media.closest('.product-gallery') || document;
    const button = gallery.querySelector(delta > 0 ? 'carousel-next-button button, carousel-next-button' : 'carousel-prev-button button, carousel-prev-button');
    button?.click();
  }, { passive: false });
})();

/* Size chips merged by letter case (M / m): keep the hidden twins in step with the visible chip.
   Capture phase, so the twins are set before the facets form reads its values. */
document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || input.hasAttribute('data-a-size-twin')) return;
  const merge = input.closest('[data-a-size-merge]');
  if (!merge) return;
  merge.querySelectorAll('[data-a-size-twin]').forEach((twin) => { twin.checked = input.checked; });
}, true);

/* Product description: lines like "S=36" and "אריאלה 1.75 לובשת מידה S" are typed one per line in the admin.
   Consecutive size lines become one row with separators, and all model lines become one row (where the first one was). */
(() => {
  const SIZE_LINE = /^\s*(XXS|XS|S|M|L|XL|XXL|XXXL)\s*[=:]\s*(.+?)\s*$/i;
  const MODEL_LINE = /לובש(ת)?\s+מידה/;

  const row = (cls, items) => {
    const p = document.createElement('p');
    p.className = `a-desc-row ${cls}`;
    items.forEach((html) => { const s = document.createElement('span'); s.innerHTML = html; p.appendChild(s); });
    return p;
  };

  const format = (prose) => {
    if (prose.hasAttribute('data-a-desc')) return;
    prose.setAttribute('data-a-desc', '');

    // one paragraph per line (lines split by <br> become their own paragraphs)
    prose.querySelectorAll(':scope > p').forEach((p) => {
      if (!p.querySelector('br')) return;
      p.innerHTML.split(/<br\s*\/?>/i).forEach((part) => {
        if (part.replace(/&nbsp;|\s/g, '') === '') return;
        const np = document.createElement('p'); np.innerHTML = part.trim(); p.before(np);
      });
      p.remove();
    });

    const paras = [...prose.querySelectorAll(':scope > p')];
    let sizeGroup = [];
    const flushSizes = () => {
      if (sizeGroup.length > 1) {
        sizeGroup[0].before(row('a-desc-sizes', sizeGroup.map((p) => {
          const [, size, value] = p.textContent.match(SIZE_LINE);
          return `<b>${size.toUpperCase()}</b> ${value.replace(/[<>&]/g, '')}`;
        })));
        sizeGroup.forEach((p) => p.remove());
      }
      sizeGroup = [];
    };
    const models = [];
    paras.forEach((p) => {
      const text = p.textContent.trim();
      if (SIZE_LINE.test(text)) { sizeGroup.push(p); return; }
      flushSizes();
      if (MODEL_LINE.test(text)) models.push(p);
    });
    flushSizes();
    if (models.length) {
      models[0].before(row('a-desc-models', models.map((p) => p.textContent.trim().replace(/[<>&]/g, ''))));
      models.forEach((p) => p.remove());
    }
  };

  const run = () => document.querySelectorAll('.product-info__block-item[data-block-type="description"] .prose').forEach(format);
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', run) : run();
  let queued = false;
  new MutationObserver(() => { if (queued) return; queued = true; requestAnimationFrame(() => { queued = false; run(); }); }).observe(document.documentElement, { childList: true, subtree: true });
})();

/* Off-ratio card images: paint the card frame with the color of the image's edges, so the added space blends in */
(() => {
  const paint = (img) => {
    if (img.dataset.aEdge) return;
    img.dataset.aEdge = '1';
    const figure = img.closest('.product-card__figure');
    if (!figure) return;
    const probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.onload = () => {
      try {
        const c = document.createElement('canvas');
        c.width = probe.naturalWidth; c.height = probe.naturalHeight;
        const ctx = c.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(probe, 0, 0);
        const pts = [[1, 1], [c.width - 2, 1], [1, c.height - 2], [c.width - 2, c.height - 2], [Math.floor(c.width / 2), 1], [Math.floor(c.width / 2), c.height - 2]];
        let r = 0, g = 0, b = 0;
        pts.forEach(([x, y]) => { const d = ctx.getImageData(x, y, 1, 1).data; r += d[0]; g += d[1]; b += d[2]; });
        figure.style.backgroundColor = `rgb(${Math.round(r / pts.length)} ${Math.round(g / pts.length)} ${Math.round(b / pts.length)})`;
      } catch (e) { /* keep the default frame color */ }
    };
    const src = img.currentSrc || img.src;
    probe.src = src.replace(/([?&])width=\d+/, '$1width=40');
  };
  const run = () => document.querySelectorAll('.product-card__image--primary.a-img-contain').forEach(paint);
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', run) : run();
  let queued = false;
  new MutationObserver(() => { if (queued) return; queued = true; requestAnimationFrame(() => { queued = false; run(); }); }).observe(document.documentElement, { childList: true, subtree: true });
})();

/* Wishlist: product handles kept in the visitor's browser. Hearts toggle, the header shows the count,
   and the wishlist page loads each card through the "anjaly-product-card" section. */
(() => {
  const KEY = 'anjaly_wishlist';
  const read = () => { try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch (e) { return []; } };
  const write = (list) => { try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) { /* storage blocked */ } };

  const paint = () => {
    const list = read();
    // Write only what changed: every write is a DOM mutation, and the observer below repaints on mutations.
    document.querySelectorAll('[data-a-wish]').forEach((btn) => {
      const on = list.includes(btn.dataset.aWish);
      const pressed = on ? 'true' : 'false';
      const label = (on ? btn.dataset.labelRemove : btn.dataset.labelAdd) || '';
      if (btn.classList.contains('is-active') !== on) btn.classList.toggle('is-active', on);
      if (btn.getAttribute('aria-pressed') !== pressed) btn.setAttribute('aria-pressed', pressed);
      if (btn.getAttribute('aria-label') !== label) btn.setAttribute('aria-label', label);
    });
    document.querySelectorAll('[data-a-wish-count]').forEach((el) => {
      const count = String(list.length);
      if (el.textContent !== count) el.textContent = count;
      if (el.hidden !== (list.length === 0)) el.hidden = list.length === 0;
    });
  };

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-a-wish]');
    if (!btn) return;
    event.preventDefault();
    event.stopPropagation();
    const handle = btn.dataset.aWish;
    const list = read();
    const i = list.indexOf(handle);
    i === -1 ? list.unshift(handle) : list.splice(i, 1);
    write(list);
    paint();
  });

  const fillPage = async () => {
    const page = document.querySelector('[data-a-wishlist]');
    if (!page || page.dataset.loaded) return;
    page.dataset.loaded = '1';
    const grid = page.querySelector('[data-a-wishlist-grid]');
    const empty = page.querySelector('[data-a-wishlist-empty]');
    const list = read();
    if (!list.length) { empty.hidden = false; return; }
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const cards = await Promise.all(list.map(async (handle) => {
      try {
        const res = await fetch(`${root}products/${encodeURIComponent(handle)}?section_id=anjaly-product-card`);
        if (!res.ok) return null;
        const html = await res.text();
        const box = document.createElement('div');
        box.innerHTML = html;
        return box.querySelector('.product-card');
      } catch (e) { return null; }
    }));
    const found = cards.filter(Boolean);
    found.forEach((card) => grid.appendChild(card));
    // drop products that no longer exist
    if (found.length !== list.length) write(list.filter((h, idx) => cards[idx]));
    if (!found.length) empty.hidden = false;
    paint();
  };

  const run = () => { paint(); fillPage(); };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', run) : run();
  window.addEventListener('storage', (e) => { if (e.key === KEY) paint(); });
  let queued = false;
  new MutationObserver(() => { if (queued) return; queued = true; requestAnimationFrame(() => { queued = false; paint(); }); }).observe(document.documentElement, { childList: true, subtree: true });
})();

/* Cart: after add, a short toast at the top and a small popup at the bottom (image, title, items, total, pay button).
   On phones the header cart icon goes to the full cart page. */
(() => {
  const cfg = window.anjalyCart || {};
  const money = (cents) => {
    const amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
    const fmt = cfg.moneyFormat || '₪{{amount}}';
    return fmt.replace(/\{\{\s*amount[a-z_]*\s*\}\}/, amount);
  };
  const check = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></svg>';
  const esc = (s) => String(s || '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  let toast, pop, timer;

  const show = (detail) => {
    if (cfg.popup === false) return;
    const item = (detail.items || [])[0];
    const cart = detail.cart || {};
    if (!toast) { toast = document.createElement('div'); toast.className = 'a-added-toast'; toast.setAttribute('role', 'status'); document.body.appendChild(toast); }
    toast.innerHTML = `${check}<span>${esc(cfg.added || 'נוסף לסל')}</span>`;
    if (!pop) { pop = document.createElement('div'); pop.className = 'a-added-pop'; document.body.appendChild(pop); }
    const img = item && (item.featured_image?.url || item.image);
    const line = (cfg.items || '[count] פריטים · [total]').replace('[count]', cart.item_count ?? '').replace('[total]', money(cart.total_price || 0));
    pop.innerHTML = `
      ${img ? `<img class="a-added-pop__img" src="${esc(img)}${img.includes('?') ? '&' : '?'}width=120" alt="">` : ''}
      <div class="a-added-pop__info">
        <p class="a-added-pop__head">${check}<span>${esc(cfg.added || 'נוסף לסל')}</span></p>
        <p class="a-added-pop__title">${esc(item ? item.product_title : '')}</p>
        <p>${esc(line)}</p>
      </div>
      <div class="a-added-pop__actions">
        <a class="a-added-pop__pay" href="${(window.Shopify?.routes?.root || '/')}checkout">${esc(cfg.pay || 'לתשלום')}</a>
        ${cfg.cart ? `<a class="a-added-pop__cart" href="${(window.Shopify?.routes?.root || '/')}cart">${esc(cfg.cart)}</a>` : ''}
      </div>
      <button type="button" class="a-added-pop__close" aria-label="סגירה"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg></button>`;
    pop.querySelector('.a-added-pop__close').addEventListener('click', () => pop.classList.remove('is-visible'));
    requestAnimationFrame(() => { toast.classList.add('is-visible'); pop.classList.add('is-visible'); });
    clearTimeout(timer);
    setTimeout(() => toast.classList.remove('is-visible'), 2200);
    timer = setTimeout(() => pop.classList.remove('is-visible'), 6000);
  };

  document.addEventListener('variant:add', (event) => {
    if (window.themeVariables?.settings?.cartType !== 'message') return;
    show(event.detail || {});
  });

  // cart icon opens the full cart page: on phones ("anjaly_mobile_cart_page") and/or desktop ("anjaly_desktop_cart_page")
  document.addEventListener('click', (event) => {
    const desktop = window.innerWidth >= 700;
    if (desktop ? cfg.desktopPage !== true : cfg.mobilePage === false) return;
    const link = event.target.closest('.header__cart-link a');
    if (!link) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    window.location.href = link.getAttribute('href');
  }, true);
})();

/* Cart page: "move to wishlist" saves the product in the wishlist, then removes the line */
document.addEventListener('click', (event) => {
  const link = event.target.closest('[data-a-move-wish]');
  if (!link) return;
  try {
    const list = JSON.parse(localStorage.getItem('anjaly_wishlist')) || [];
    if (!list.includes(link.dataset.aMoveWish)) list.unshift(link.dataset.aMoveWish);
    localStorage.setItem('anjaly_wishlist', JSON.stringify(list));
  } catch (e) { /* storage blocked: the line is still removed */ }
});

/* Cashback "i": opens the tiers explanation in a fixed popover kept inside the viewport */
(() => {
  let pop = null;
  const close = () => { if (pop) { pop.remove(); pop = null; } };
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-a-cashback-info]');
    if (!btn) { if (pop && !event.target.closest('.a-cb-pop')) close(); return; }
    event.preventDefault();
    event.stopPropagation();
    if (pop) { close(); return; }
    const tpl = btn.parentElement.querySelector('template[data-a-cashback-pop]');
    if (!tpl) return;
    pop = document.createElement('div');
    pop.className = 'a-cb-pop';
    pop.setAttribute('role', 'dialog');
    pop.appendChild(tpl.content.cloneNode(true));
    document.body.appendChild(pop);
    const r = btn.getBoundingClientRect();
    const w = pop.offsetWidth, h = pop.offsetHeight;
    let left = Math.min(Math.max(12, r.left + r.width / 2 - w / 2), window.innerWidth - w - 12);
    let top = r.bottom + 8;
    if (top + h > window.innerHeight - 12) top = Math.max(12, r.top - h - 8);
    pop.style.left = `${left}px`;
    pop.style.top = `${top}px`;
  }, true);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
  window.addEventListener('scroll', close, { passive: true });
})();

/* Gift card module: phone carousel (swipe is native scroll-snap), dots and optional autoplay */
(() => {
  const init = (track) => {
    if (track.dataset.ready) return;
    track.dataset.ready = '1';
    const dots = track.parentElement.querySelectorAll('[data-a-gc-dots] button');
    const items = [...track.children];
    const index = () => Math.round(Math.abs(track.scrollLeft) / (track.clientWidth || 1));
    const paint = () => dots.forEach((d, i) => d.classList.toggle('is-active', i === index()));
    const go = (i) => items[i] && track.scrollTo({ left: items[i].offsetLeft - track.offsetLeft, behavior: 'smooth' });
    dots.forEach((d, i) => d.addEventListener('click', () => go(i)));
    track.addEventListener('scroll', () => requestAnimationFrame(paint), { passive: true });
    paint();
    if (track.dataset.autoplay === 'true' && items.length > 1 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      let paused = false;
      track.addEventListener('touchstart', () => { paused = true; }, { passive: true });
      setInterval(() => {
        if (paused || window.innerWidth >= 700 || document.hidden) return;
        go((index() + 1) % items.length);
      }, Math.max(1000, Number(track.dataset.interval) || 3500));
    }
  };
  const run = () => document.querySelectorAll('[data-a-gc]').forEach(init);
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', run) : run();
  document.addEventListener('shopify:section:load', run);
})();

/* Community (phone): dot indicators under the swipe carousel */
(() => {
  const init = (carousel) => {
    if (carousel.dataset.aDots) return;
    const items = [...carousel.children].filter((el) => el.nodeType === 1);
    if (items.length < 2) return;
    carousel.dataset.aDots = '1';
    const dots = document.createElement('div');
    dots.className = 'a-community-dots';
    items.forEach((item, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.setAttribute('aria-label', String(i + 1));
      b.addEventListener('click', () => item.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' }));
      dots.appendChild(b);
    });
    carousel.after(dots);
    const paint = () => {
      const box = carousel.getBoundingClientRect();
      const mid = box.left + box.width / 2;
      let best = 0, bestDist = Infinity;
      items.forEach((item, i) => {
        const r = item.getBoundingClientRect();
        const d = Math.abs(r.left + r.width / 2 - mid);
        if (d < bestDist) { bestDist = d; best = i; }
      });
      [...dots.children].forEach((d, i) => d.classList.toggle('is-active', i === best));
    };
    carousel.addEventListener('scroll', () => requestAnimationFrame(paint), { passive: true });
    paint();
  };
  const run = () => document.querySelectorAll('.shopify-section--community-gallery scroll-carousel').forEach(init);
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', run) : run();
  document.addEventListener('shopify:section:load', run);
})();

/* Promo / countdown bar: Israel time; before start / live / after end; replaces the announcement bar while shown */
(() => {
  const tzOffsetMs = (date) => {
    const p = new Intl.DateTimeFormat('en-US', { timeZone: 'Asia/Jerusalem', hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }).formatToParts(date);
    const g = (t) => Number(p.find((x) => x.type === t).value);
    return Date.UTC(g('year'), g('month') - 1, g('day'), g('hour'), g('minute'), g('second')) - date.getTime();
  };
  const parseIsrael = (text) => {
    const m = String(text || '').trim().match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2}))?$/);
    if (!m) return null;
    const asUtc = Date.UTC(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0));
    return asUtc - tzOffsetMs(new Date(asUtc));
  };
  const pad = (n) => String(n).padStart(2, '0');
  const init = (bar) => {
    if (bar.dataset.ready) return;
    bar.dataset.ready = '1';
    const start = bar.dataset.startMs ? Number(bar.dataset.startMs) : parseIsrael(bar.dataset.start);
    const end = bar.dataset.endMs ? Number(bar.dataset.endMs) : parseIsrael(bar.dataset.end);
    if (!end) {
      // no end date: text (and coupon) only, no clock
      bar.querySelectorAll('[data-a-promo-pre]').forEach((el) => { el.hidden = true; });
      bar.querySelectorAll('[data-a-promo-live]').forEach((el) => { el.hidden = false; });
      bar.hidden = false;
      document.body.classList.add('a-promo-on');
      return;
    }
    const q = (s) => bar.querySelector(s);
    const tick = () => {
      const now = Date.now();
      if (now >= end) { bar.hidden = true; document.body.classList.remove('a-promo-on'); return false; }
      const live = !start || now >= start;
      const target = live ? end : start;
      let left = Math.max(0, Math.floor((target - now) / 1000));
      const d = Math.floor(left / 86400); left %= 86400;
      const h = Math.floor(left / 3600); left %= 3600;
      q('[data-a-promo-d]').textContent = pad(d);
      q('[data-a-promo-h]').textContent = pad(h);
      q('[data-a-promo-m]').textContent = pad(Math.floor(left / 60));
      q('[data-a-promo-s]').textContent = pad(left % 60);
      bar.classList.toggle('is-live', live);
      bar.querySelectorAll('[data-a-promo-pre]').forEach((el) => { el.hidden = live; });
      bar.querySelectorAll('[data-a-promo-live]').forEach((el) => { el.hidden = !live; });
      bar.hidden = false;
      document.body.classList.add('a-promo-on');
      return true;
    };
    if (tick()) { const t = setInterval(() => { if (!tick()) clearInterval(t); }, 1000); }
  };
  const run = () => document.querySelectorAll('[data-a-promo]').forEach(init);
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', run) : run();
  document.addEventListener('shopify:section:load', run);
})();

/* Valuecard switches: Content > Metaobjects > "וואליו קארד - הגנות ותוספות", printed by snippets/anjaly-vc-copy.liquid
   as window.anjalyVC. Checked at the moment each piece acts. No switches on the page = all on. */
window.anjalyVcOn = (key) => !window.anjalyVC || window.anjalyVC[key] === true;
/* All switches off (or the club is off in this theme) = none of our Valuecard code runs: no listeners, no observers, no cart reads. */
window.anjalyVcOff = !!window.anjalyVC && !Object.values(window.anjalyVC).some(Boolean);

/* Club bar under the menu (Theme settings > "מיקום פס המועדון"): move Valuecard's bar right after the header.
   The same element is moved, so Valuecard's own clicks keep working. */
(() => {
  if (!document.documentElement.classList.contains('a-vc-top')) return;
  const move = () => {
    const bar = document.querySelector('#vcPlugin');
    if (!bar) return false;
    const block = bar.closest('.shopify-app-block') || bar;
    const header = document.querySelector('.shopify-section--header');
    if (header) {
      if (header.nextElementSibling !== block) header.after(block);
      if (document.documentElement.classList.contains('a-vc-sticky') && !block.classList.contains('a-vc-bar-wrap')) {
        // Sticky under the (sticky) header: top = where the header sits + its height
        block.classList.add('a-vc-bar-wrap');
        const setTop = () => document.documentElement.style.setProperty('--a-vc-top', ((parseFloat(getComputedStyle(header).top) || 0) + header.offsetHeight) + 'px');
        setTop();
        new ResizeObserver(setTop).observe(header);
        window.addEventListener('resize', setTop);
      }
    }
    else if (document.body.firstElementChild !== block) document.body.prepend(block);
    return true;
  };
  if (move()) return;
  const mo = new MutationObserver(() => { if (move()) mo.disconnect(); });
  mo.observe(document.documentElement, { childList: true, subtree: true });
  setTimeout(() => mo.disconnect(), 20000);
})();

/* Valuecard bridge: while Valuecard is the club (its widget is on the page and the Anjaly Club app is not),
   the theme's club buttons open the Valuecard window: header JOIN THE CLUB, cart club box buttons,
   the product page club line, the side cart club note. They tap Valuecard's own button, which picks the step. */
(() => {
  if (window.anjalyVcOff) return; // every switch off = Valuecard runs alone, nothing of ours
  const ready = () => !document.getElementById('anjaly-club-root')
    && window.vcWidget && typeof window.vcWidget.open === 'function'
    && !!document.getElementById('vcPlugin'); // Valuecard hides its button while the side cart is open, so don't require it visible
  document.addEventListener('click', (event) => {
    if (event.defaultPrevented) return;
    const opener = event.target.closest('[data-club-open], .header__cta-area [aria-controls^="club-modal-"]');
    if (!opener || !window.anjalyVcOn('open_links') || !ready()) return;
    event.preventDefault();
    event.stopPropagation();
    // Same as tapping Valuecard's own floating button (vcWidget.open() alone does not always show the window).
    const drawer = document.querySelector('cart-drawer[open]');
    const tap = () => document.getElementById('vcPlugin').click();
    if (drawer && typeof drawer.hide === 'function') { drawer.hide(); setTimeout(tap, 400); } else tap();
  }, true);
})();

/* Club app bridge: when the Anjaly Club app embed is on (#anjaly-club-root), the header
   "JOIN THE CLUB" button opens the app's popup instead of the theme's old club modal.
   Links marked data-club-open (cart club box, product club line) are opened by the app itself.
   Without the app nothing changes. */
(() => {
  const run = () => {
    if (!document.getElementById('anjaly-club-root')) return;
    document.querySelectorAll('.header__cta-area [aria-controls^="club-modal-"]').forEach((btn) => {
      btn.removeAttribute('aria-controls');
      btn.setAttribute('data-club-open', '');
    });
  };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', run) : run();
  document.addEventListener('shopify:section:load', run);
})();

/* Sitewide sale (snippets/anjaly-sitewide-sale.liquid): popup, floating copy widget.
   In "בלי המועדון" mode, club links (/pages/club, data-club-open) open the popup instead. */
(() => {
  const DAY = 86400000;
  const store = (kind, key, value) => {
    try {
      const s = kind === 'local' ? localStorage : sessionStorage;
      if (value === undefined) return s.getItem(key);
      s.setItem(key, value);
    } catch (e) { return null; }
    return null;
  };
  const dialog = () => document.querySelector('[data-a-sws-pop]');
  const open = () => {
    const d = dialog();
    if (!d || d.open) return false;
    if (d.showModal) d.showModal(); else d.setAttribute('open', '');
    return true;
  };
  const close = () => { const d = dialog(); if (d && d.open) d.close(); };

  document.addEventListener('click', (event) => {
    const d = dialog();
    if (!d) return;
    if (event.target === d) { close(); return; }
    if (event.target.closest('[data-a-sws-close]')) { close(); return; }
    const opener = d.dataset.clubLinks === 'true'
      ? event.target.closest('[data-a-sws-open], [data-club-open], a[href$="/pages/club"], a[href*="/pages/club?"]')
      : event.target.closest('[data-a-sws-open]');
    if (opener && !event.target.closest('[data-a-copy]')) {
      event.preventDefault();
      open();
      return;
    }
    if (event.target.closest('[data-a-sws-float-close]')) {
      const f = document.querySelector('[data-a-sws-float]');
      if (f) f.hidden = true;
      store('session', 'anjaly_sws_float_closed_' + (f ? f.dataset.sale : ''), '1');
    }
  }, true);

  const run = () => {
    const f = document.querySelector('[data-a-sws-float]');
    if (f && store('session', 'anjaly_sws_float_closed_' + f.dataset.sale) !== '1') f.hidden = false;
    const d = dialog();
    if (!d || d.dataset.auto !== 'true') return;
    const key = 'anjaly_sws_popup_' + d.dataset.sale;
    const last = Number(store('local', key) || 0);
    if (Date.now() - last < 30 * DAY) return;
    setTimeout(() => { if (open()) store('local', key, String(Date.now())); }, Number(d.dataset.delay || 8) * 1000);
  };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', run) : run();
})();

/* Valuecard: the club app applies the member's benefit in the background (GET /discount/CODE) after every cart
   change, but the side cart has already re-rendered by then, so it showed the cart without the benefit.
   When that request finishes, refresh the side cart and the cart count. */
(() => {
  if (window.anjalyVcOff) return; // every switch off = Valuecard runs alone, nothing of ours
  let timer;
  const refresh = () => {
    clearTimeout(timer);
    timer = setTimeout(() => document.dispatchEvent(new CustomEvent('cart:refresh')), 250);
  };
  const isDiscount = (url) => {
    try { return new URL(url, location.origin).pathname.startsWith('/discount/'); } catch (e) { return false; }
  };
  const open = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (isDiscount(url)) this.addEventListener('loadend', () => { if (window.anjalyVcOn('refresh_cart')) refresh(); });
    return open.apply(this, arguments);
  };
  const origFetch = window.fetch;
  window.fetch = function (input) {
    const res = origFetch.apply(this, arguments);
    const url = typeof input === 'string' ? input : input && input.url;
    if (url && isDiscount(url)) res.finally(() => { if (window.anjalyVcOn('refresh_cart')) refresh(); });
    return res;
  };
})();

/* Valuecard club copy: mark the page when the Valuecard button is showing (html.a-has-vc), and add the
   theme-setting line ("צוברות נקודות...") as a small second line inside Valuecard's pill button. */
(() => {
  if (window.anjalyVcOff) return; // every switch off = Valuecard runs alone, nothing of ours
  const sync = () => {
    const btn = document.getElementById('vcPlugin');
    const on = !!btn; // present = Valuecard is the club (it hides the button itself at times, e.g. over the side cart)
    document.documentElement.classList.toggle('a-has-vc', on);
    const src = document.querySelector('[data-vc-tip]');
    if (on && !window.anjalyVcOn('pill_line')) {
      const old = btn.querySelector('.a-vc-sub');
      if (old) { old.remove(); btn.classList.remove('a-vc-has-sub'); }
      return;
    }
    if (!on || !src || btn.querySelector('.a-vc-sub')) return;
    const label = btn.querySelector('span') || btn;
    const sub = document.createElement('small');
    sub.className = 'a-vc-sub';
    sub.textContent = src.textContent.trim();
    label.appendChild(sub);
    btn.classList.add('a-vc-has-sub');
  };
  let tries = 0;
  const wait = setInterval(() => { sync(); if ((document.querySelector('#vcPlugin .a-vc-sub') && window.anjalyVcOn('pill_line')) || ++tries > 40) clearInterval(wait); }, 500);
  // Valuecard may redraw its button; put the line back.
  new MutationObserver(sync).observe(document.body, { childList: true });
})();

/* Valuecard benefit code. After a member redeems points, Valuecard calls /checkout?discount=CODE and only
   then /discount/CODE - but Shopify answers the first call with 403, so the code never reached the cart
   (no discount in the side cart, the cart page or checkout). When we see that call, we put the code on the
   cart ourselves. Only if it still isn't on the cart (as a backup) we show it with a copy button, in the club
   pill and in the cart boxes [data-vc-code]. */
(() => {
  if (window.anjalyVcOff) return; // every switch off = Valuecard runs alone, nothing of ours
  const readStored = () => {
    let raw = null;
    try {
      for (let i = 0; i < sessionStorage.length; i++) {
        const k = sessionStorage.key(i);
        if (/StoredDiscount$/.test(k)) { raw = sessionStorage.getItem(k); break; }
      }
    } catch (e) {}
    if (!raw) {
      const m = document.cookie.match(/(?:^|;\s*)[^=;]*StoredDiscount=([^;]*)/);
      if (m) { try { raw = decodeURIComponent(m[1]); } catch (e) { raw = m[1]; } }
    }
    if (!raw) return null;
    try {
      const d = JSON.parse(raw);
      return d && d.discountCode && !d.isError ? { code: String(d.discountCode), amount: parseFloat(d.totalDiscount) || 0 } : null;
    } catch (e) { return null; }
  };
  let current = null;
  const settings = () => document.querySelector('[data-vc-tip]');
  const copy = (code, btn) => {
    const done = () => {
      const s = settings();
      if (!btn) return;
      const was = btn.dataset.label || btn.textContent;
      btn.dataset.label = was;
      btn.textContent = (s && s.dataset.copied) || 'הועתק';
      setTimeout(() => { btn.textContent = was; }, 1600);
    };
    (navigator.clipboard ? navigator.clipboard.writeText(code) : Promise.reject()).then(done).catch(() => {
      const t = document.createElement('textarea'); t.value = code; document.body.appendChild(t); t.select();
      try { document.execCommand('copy'); done(); } catch (e) {} t.remove();
    });
  };
  // Backup only: show the code when it did NOT reach the cart (missing, or Shopify marks it not applicable).
  let onCart = false;
  let others = []; // other codes on the cart; Shopify may refuse the club code next to them (no combining)
  const check = async () => {
    // Read the cart only when one of the switches that use it is on (9 backup code, 10 coupon conflict)
    if (!current || !(window.anjalyVcOn('backup_code') || window.anjalyVcOn('conflict_message'))) { onCart = false; others = []; return; }
    try {
      const cart = await (await fetch('/cart.js', { credentials: 'same-origin' })).json();
      onCart = (cart.discount_codes || []).some((d) => d.applicable && d.code.toUpperCase() === current.code.toUpperCase());
      others = (cart.discount_codes || []).filter((d) => d.applicable && !/^VC_/i.test(d.code)).map((d) => d.code);
    } catch (e) { onCart = false; others = []; }
  };
  const render = () => {
    const vc = current && !onCart && window.anjalyVcOn('backup_code') ? current : null;
    document.documentElement.classList.toggle('a-vc-has-code', !!vc);
    document.documentElement.classList.toggle('a-vc-member', !!current);
    document.querySelectorAll('[data-vc-code]').forEach((box) => {
      if (box.hidden !== !vc) box.hidden = !vc;
      if (!vc) return;
      const val = box.querySelector('[data-vc-code-value]');
      if (val.textContent !== vc.code) val.textContent = vc.code;
      const amt = box.querySelector('[data-vc-code-amount]');
      const amtText = vc.amount ? '(' + vc.amount.toLocaleString('he-IL') + ' ₪)' : '';
      if (amt && amt.textContent !== amtText) amt.textContent = amtText;
      const conflict = box.querySelector('[data-vc-code-conflict]');
      const swap = box.querySelector('[data-vc-code-swap]');
      const note = box.querySelector('[data-vc-code-note]');
      const other = window.anjalyVcOn('conflict_message') ? others[0] : null;
      if (conflict) {
        const text = other ? (conflict.dataset.text || '').split('{code}').join(other) : '';
        if (conflict.textContent !== text) conflict.textContent = text;
        if (conflict.hidden !== !other) conflict.hidden = !other;
      }
      if (swap) {
        const text = other ? (swap.dataset.text || '').split('{code}').join(other) : '';
        if (swap.textContent !== text) swap.textContent = text;
        if (swap.hidden !== !other) swap.hidden = !other;
      }
      if (note && note.hidden !== !!other) note.hidden = !!other;
    });
    const sub = document.querySelector('#vcPlugin .a-vc-sub');
    const s = settings();
    if (!sub || !s) return;
    if (!sub.dataset.plain) sub.dataset.plain = sub.textContent;
    const text = vc ? ((s.dataset.codeLabel || '') + ' ' + vc.code).trim() : sub.dataset.plain;
    if (sub.textContent !== text) sub.textContent = text;
    sub.classList.toggle('a-vc-sub--code', !!vc);
  };
  const refreshCart = () => document.dispatchEvent(new CustomEvent('cart:refresh'));
  const applied = new Set();
  const apply = async (codes) => {
    const list = codes.split(',').map((c) => c.trim()).filter(Boolean);
    const vcCode = list.find((c) => /^VC_/i.test(c));
    if (!vcCode) return;
    let removed = null;
    try { removed = sessionStorage.getItem('anjaly_vc_removed'); } catch (e) {}
    if (removed && removed.toUpperCase() === vcCode.toUpperCase()) return; // the shopper took it off herself
    try {
      const cart = await (await fetch('/cart.js', { credentials: 'same-origin' })).json();
      // Valuecard issues a new code after every cart change and deletes the old one, so a code already on the
      // cart may be dead. Replace it unless this exact code is already there. Loop guards: we write the cart at
      // most once per page, and reload the cart page at most once per visit.
      const same = (cart.discount_codes || []).some((d) => d.applicable && d.code.toUpperCase() === vcCode.toUpperCase());
      const oldVc = (cart.discount_codes || []).some((d) => /^VC_/i.test(d.code) && d.code.toUpperCase() !== vcCode.toUpperCase());
      const write = window.anjalyVcOn('attach_code') && (!oldVc || window.anjalyVcOn('replace_old_code'));
      if (write && !same && applied.size === 0) {
        applied.add(vcCode);
        const others = (cart.discount_codes || []).map((d) => d.code).filter((c) => !/^VC_/i.test(c) && !list.includes(c));
        await fetch('/cart/update.js', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: list.concat(others).join(',') })
        });
        let reloaded = false;
        try { reloaded = sessionStorage.getItem('anjaly_vc_cart_reload') === vcCode; } catch (e) {}
        if (/\/cart\/?$/.test(location.pathname) && !reloaded) {
          try { sessionStorage.setItem('anjaly_vc_cart_reload', vcCode); } catch (e) {}
          location.reload();
          return;
        }
      }
      // Valuecard's own call may also have changed the cart's codes: redraw the side cart so its lines are current.
      if (window.anjalyVcOn('refresh_cart')) refreshCart();
    } catch (e) {}
    current = readStored() || { code: vcCode, amount: 0 };
    await check();
    render();
  };
  const open = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    try {
      const u = new URL(url, location.origin);
      if (u.pathname === '/checkout' && u.searchParams.get('discount')) {
        const codes = u.searchParams.get('discount');
        this.addEventListener('loadend', () => apply(codes));
      }
    } catch (e) {}
    return open.apply(this, arguments);
  };
  // Put exactly these codes on the cart, then show the result.
  const setCodes = async (codes) => {
    try {
      await fetch('/cart/update.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: codes.join(',') })
      });
    } catch (e) {}
    if (/\/cart\/?$/.test(location.pathname)) { location.reload(); return; }
    refreshCart();
    await check();
    render();
  };
  // Remove one code from its chip ([data-a-remove-code], cart page). A removed club code is not put back this visit.
  document.addEventListener('click', async (event) => {
    const chip = event.target.closest('[data-a-remove-code]');
    if (!chip) return;
    event.preventDefault();
    const code = chip.dataset.aRemoveCode;
    if (/^VC_/i.test(code)) { try { sessionStorage.setItem('anjaly_vc_removed', code); } catch (e) {} }
    try {
      const cart = await (await fetch('/cart.js', { credentials: 'same-origin' })).json();
      setCodes((cart.discount_codes || []).map((d) => d.code).filter((c) => c.toUpperCase() !== code.toUpperCase()));
    } catch (e) {}
  });
  // Log out in the Valuecard window (.btn-reset-login): take the club codes off the cart, keep any other code.
  const dropVcCodes = async () => {
    current = null;
    applied.clear();
    try {
      const cart = await (await fetch('/cart.js', { credentials: 'same-origin' })).json();
      const codes = (cart.discount_codes || []).map((d) => d.code);
      if (codes.some((c) => /^VC_/i.test(c))) {
        await fetch('/cart/update.js', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: codes.filter((c) => !/^VC_/i.test(c)).join(',') })
        });
        try { sessionStorage.removeItem('anjaly_vc_cart_reload'); } catch (e) {}
        if (/\/cart\/?$/.test(location.pathname)) { location.reload(); return; }
        refreshCart();
      }
    } catch (e) {}
    onCart = false;
    render();
  };
  document.addEventListener('click', (event) => {
    if (event.target.closest('.btn-reset-login')) { if (window.anjalyVcOn('logout_cleanup')) setTimeout(dropVcCodes, 400); return; }
    const swapBtn = event.target.closest('[data-vc-code-swap]');
    if (swapBtn && current) {
      event.preventDefault();
      try { sessionStorage.removeItem('anjaly_vc_removed'); } catch (e) {}
      setCodes([current.code]);
      return;
    }
    const btn = event.target.closest('[data-vc-code-copy]');
    if (btn && current) { event.preventDefault(); copy(current.code, btn); return; }
    const pill = event.target.closest('#vcPlugin .a-vc-sub--code');
    if (pill && current) { event.preventDefault(); event.stopPropagation(); copy(current.code, null); pill.textContent = (settings() && settings().dataset.copied) || 'הועתק'; setTimeout(render, 1600); }
  }, true);
  const boot = async () => { const stored = readStored(); if (stored) current = stored; await check(); render(); };
  ['cart:refresh', 'cart:change'].forEach((name) => document.addEventListener(name, () => setTimeout(async () => { await check(); render(); }, 800)));
  let tries = 0;
  const wait = setInterval(() => { boot(); if (++tries > 20) clearInterval(wait); }, 500);
  // The side cart redraws itself; fill the new box.
  // Only when a backup box should show and a freshly drawn one is still hidden (never in the normal case).
  const drawer = document.querySelector('cart-drawer');
  if (drawer) new MutationObserver(() => { if (current && !onCart && drawer.querySelector('[data-vc-code][hidden]')) render(); })
    .observe(drawer, { childList: true, subtree: true });
})();

/* Valuecard balance wording (metaobject switch 13): Valuecard writes one after-login text on its button
   ("צברת כבר [Balance] ₪"). We read the balance from it and show the theme-setting text: zero / below /
   above the redemption threshold, or, once the member redeemed, the amount she chose in the club window
   (Valuecard keeps it in its StoredDiscount). Writes only when the text changes (Valuecard may rewrite it). */
(() => {
  if (window.anjalyVcOff) return; // every switch off = Valuecard runs alone, nothing of ours
  const num = (t) => { const m = String(t).replace(/,/g, '').match(/(\d+(?:\.\d+)?)/); return m ? parseFloat(m[1]) : null; };
  const fmt = (n) => (Math.round(n * 100) / 100).toLocaleString('he-IL', { maximumFractionDigits: 2 });
  // Amount the member chose to redeem (Valuecard's stored discount), or 0
  const redeemed = () => {
    let raw = null;
    try {
      for (let i = 0; i < sessionStorage.length; i++) {
        const k = sessionStorage.key(i);
        if (/StoredDiscount$/.test(k)) { raw = sessionStorage.getItem(k); break; }
      }
    } catch (e) {}
    if (!raw) {
      const m = document.cookie.match(/(?:^|;\s*)[^=;]*StoredDiscount=([^;]*)/);
      if (m) { try { raw = decodeURIComponent(m[1]); } catch (e) { raw = m[1]; } }
    }
    try {
      const d = raw && JSON.parse(raw);
      return d && d.discountCode && !d.isError ? parseFloat(d.totalDiscount) || 0 : 0;
    } catch (e) { return 0; }
  };
  let ours = '';
  let shown = '';
  let lastBal = null;
  let lastUsed = 0;
  // Bar = text + a call to action on its own line on the phone ("למימוש ההטבה ←"), one per state
  const render = (span, text, cta) => {
    const first = span.firstChild && span.firstChild.nodeType === 3 ? span.firstChild : null;
    const link = span.querySelector('.a-vc-cta');
    const ctaText = cta ? cta + ' ←' : '';
    if (first && first.textContent === text && (link ? link.textContent : '') === ctaText) return;
    if (first) first.textContent = text; else span.textContent = text;
    const old = span.querySelector('.a-vc-cta');
    if (!cta) { if (old) old.remove(); return; }
    const el = old || document.createElement('span');
    el.className = 'a-vc-cta';
    el.innerHTML = '<u></u> ←';
    el.firstChild.textContent = cta;
    if (!old) span.appendChild(el);
    const btn = document.getElementById('vcPlugin');
    if (btn) btn.setAttribute('aria-label', text + ' - ' + cta);
  };
  let guest = false;
  const apply = () => {
    if (!window.anjalyVcOn || !window.anjalyVcOn('balance_text')) return;
    // A new redemption (or its removal) changes the text even if Valuecard did not touch its button
    const u = redeemed();
    if (u !== lastUsed) { lastUsed = u; ours = ''; }
    const span = document.querySelector('#vcPlugin span');
    const cfg = document.querySelector('[data-vc-tip]');
    if (!span || !cfg) return;
    const current = span.firstChild && span.firstChild.nodeType === 3 ? span.firstChild.textContent : span.textContent;
    if (current === ours && span.querySelector('.a-vc-cta')) return;
    // Valuecard's after-login text carries the balance (₪ and a number); the before-login text has no ₪.
    // Our own text replaced Valuecard's, so keep the state we read last time to re-render it.
    if (current !== shown) {
      guest = !/₪/.test(current);
      lastBal = guest ? null : num(current);
    }
    // Logged in: the cart note asking to log in is no longer relevant (anjaly.css hides it)
    document.documentElement.classList.toggle('a-vc-in', !guest);
    let text, cta;
    if (guest) {
      text = cfg.dataset.barGuest;
      cta = cfg.dataset.ctaGuest;
      if (!text) return;
    } else {
      const bal = lastBal;
      if (bal === null) return;
      const min = parseFloat(cfg.dataset.balMin) || 50;
      const used = redeemed();
      let tpl = bal >= min ? cfg.dataset.balHigh : cfg.dataset.balLow;
      cta = bal >= min ? cfg.dataset.ctaHigh : cfg.dataset.ctaLow;
      if (bal === 0 && cfg.dataset.balZero) { tpl = cfg.dataset.balZero; cta = cfg.dataset.ctaZero; }
      if (used && cfg.dataset.balUsed) { tpl = cfg.dataset.balUsed; cta = cfg.dataset.ctaUsed; }
      if (!tpl) return;
      text = tpl.split('{balance}').join(fmt(bal)).split('{min}').join(fmt(min)).split('{amount}').join(fmt(used || 0));
      // "ויקי, צברת..." when the Shopify account has a first name
      const name = (cfg.dataset.name || '').trim();
      if (name && cfg.dataset.namePrefix) text = cfg.dataset.namePrefix.split('{name}').join(name) + text;
    }
    ours = text;
    shown = text;
    render(span, text, cta);
  };
  ['cart:refresh', 'cart:change'].forEach((name) => document.addEventListener(name, () => setTimeout(apply, 800)));
  setInterval(apply, 2000);
  let tries = 0;
  const wait = setInterval(() => { apply(); if (++tries > 40) clearInterval(wait); }, 500);
  const hook = () => {
    const btn = document.getElementById('vcPlugin');
    if (!btn) return false;
    new MutationObserver(apply).observe(btn, { childList: true, subtree: true, characterData: true });
    return true;
  };
  if (!hook()) { const w = setInterval(() => { if (hook()) clearInterval(w); }, 500); setTimeout(() => clearInterval(w), 20000); }
})();

/* Valuecard club window: the redemption threshold under the amount field (metaobject switch 11, with the skin).
   Valuecard draws the window itself and does not say from how much one can redeem. */
(() => {
  if (window.anjalyVcOff) return; // every switch off = Valuecard runs alone, nothing of ours
  const add = () => {
    if (!window.anjalyVcOn || !window.anjalyVcOn('skin')) return;
    const cfg = document.querySelector('[data-vc-tip]');
    if (!cfg || !cfg.dataset.minLine) return;
    document.querySelectorAll('.popbox-wrapper .balance-wrapper .input-wrapper').forEach((row) => {
      if (row.nextElementSibling && row.nextElementSibling.classList.contains('a-vc-min')) return;
      const p = document.createElement('p');
      p.className = 'a-vc-min';
      p.textContent = cfg.dataset.minLine.split('{min}').join(cfg.dataset.balMin || '50');
      row.after(p);
    });
    perk(cfg);
    help(cfg);
  };
  // Under the login code boxes: "didn't get a code?" - send again (Valuecard's own send button) or WhatsApp
  const help = (cfg) => {
    if (!cfg.dataset.helpQ) return;
    document.querySelectorAll('.popbox-wrapper .insert-authcode').forEach((step) => {
      if (step.querySelector('.a-vc-help')) return;
      const box = document.createElement('div');
      box.className = 'a-vc-help';
      const q = document.createElement('p');
      q.textContent = cfg.dataset.helpQ;
      box.appendChild(q);
      const links = document.createElement('p');
      links.className = 'a-vc-help__links';
      const wrap = step.closest('.popbox-wrapper');
      const send = wrap && wrap.querySelector('.btn-verify-phonenumber, .btn-send-auth');
      if (send && cfg.dataset.helpResend) {
        const again = document.createElement('button');
        again.type = 'button';
        again.className = 'a-vc-help__link';
        again.textContent = cfg.dataset.helpResend;
        again.addEventListener('click', () => send.click());
        links.appendChild(again);
      }
      if (cfg.dataset.helpPhone && cfg.dataset.helpWa) {
        const wa = document.createElement('a');
        wa.className = 'a-vc-help__link';
        wa.href = 'https://wa.me/' + cfg.dataset.helpPhone.replace(/\D/g, '') + (cfg.dataset.helpMsg ? '?text=' + encodeURIComponent(cfg.dataset.helpMsg) : '');
        wa.target = '_blank';
        wa.rel = 'noopener';
        wa.textContent = cfg.dataset.helpWa;
        links.appendChild(wa);
      }
      box.appendChild(links);
      step.appendChild(box);
    });
  };
  // Main tab: the base benefit and what this cart will earn; and hide Valuecard's empty summary (0.00)
  let cartTotal = null;
  const loadCart = () => fetch('/cart.js', { credentials: 'same-origin' }).then((r) => r.json())
    .then((c) => { cartTotal = c.total_price / 100; perk(document.querySelector('[data-vc-tip]')); }).catch(() => {});
  const perk = (cfg) => {
    if (!cfg) return;
    const rate = parseFloat(cfg.dataset.rate) || 0;
    const min = cfg.dataset.balMin || '50';
    // \u2066...\u2069 keeps "5%" in order inside a Hebrew line (otherwise it shows as "%5")
    const fill = (s) => (s || '').split('{rate}').join(rate).split('{min}').join(min).replace(/(\d+(?:\.\d+)?%)/g, '\u2066$1\u2069');
    document.querySelectorAll('.popbox-wrapper.main-window .user-card').forEach((card) => {
      let box = card.parentElement.querySelector('.a-vc-perk');
      if (!box) {
        box = document.createElement('div');
        box.className = 'a-vc-perk';
        box.innerHTML = '<p class="a-vc-perk__title"></p><p class="a-vc-perk__cart"></p><p class="a-vc-perk__note"></p>';
        card.after(box);
      }
      const earn = cartTotal ? Math.round(cartTotal * rate) / 100 : 0;
      const set = (sel, text) => { const el = box.querySelector(sel); if (el.textContent !== text) el.textContent = text; el.hidden = !text; };
      set('.a-vc-perk__title', fill(cfg.dataset.perkTitle));
      set('.a-vc-perk__cart', earn > 0 ? fill(cfg.dataset.perkCart).split('{amount}').join(earn.toLocaleString('he-IL', { maximumFractionDigits: 2 })) : '');
      set('.a-vc-perk__note', fill(cfg.dataset.perkNote));
    });
    document.querySelectorAll('.popbox-wrapper #cartDiscountSum').forEach((sum) => {
      const tag = sum.querySelector('.footer-sum-list .price-tag');
      const zero = tag && parseFloat(tag.textContent.replace(/[^\d.]/g, '')) === 0;
      sum.classList.toggle('a-vc-sum-empty', !!zero);
    });
  };
  // Only body's direct children: Valuecard appends its window there
  new MutationObserver(() => setTimeout(add, 50)).observe(document.body, { childList: true });
  // Read the cart only while the skin is on (switch 11), and only when the Valuecard window opens, not on every click inside it
  const skinOn = () => window.anjalyVcOn && window.anjalyVcOn('skin');
  document.addEventListener('click', (e) => { if (skinOn() && e.target.closest('#vcPlugin, [data-club-open]')) { loadCart(); setTimeout(add, 600); setTimeout(add, 1500); } }, true);
  ['cart:refresh', 'cart:change'].forEach((name) => document.addEventListener(name, () => { if (skinOn()) setTimeout(loadCart, 600); }));
})();

/* Discount code field (cart page and side cart): Prestige applies a code only when the field loses focus, and Enter
   submits the whole cart form to checkout. Here the "למימוש הקופון" button and Enter apply the code in place:
   update the cart, then redraw the cart page section (or the side cart) and show the result. */
(() => {
  const fieldOf = (el) => el && el.closest('.cart-discount');
  const inputOf = (box) => box.querySelector('cart-discount-field input:not([type="hidden"])');
  // We own applying: stop Prestige's own "apply on change" so a code is not sent twice
  document.addEventListener('change', (e) => {
    if (e.target.matches && e.target.matches('cart-discount-field input:not([type="hidden"])')) e.stopPropagation();
  }, true);
  const redraw = async () => {
    const section = document.querySelector('.a-summary') && document.querySelector('.a-summary').closest('[id^="shopify-section-"]');
    if (section && /\/cart\/?$/.test(location.pathname)) {
      try {
        const id = section.id.replace('shopify-section-', '');
        const html = await (await fetch(location.pathname + '?section_id=' + encodeURIComponent(id), { credentials: 'same-origin' })).text();
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        const fresh = tmp.querySelector('#' + CSS.escape(section.id)) || tmp.firstElementChild;
        if (fresh) { section.replaceWith(fresh); return fresh; }
      } catch (e) {}
      location.reload();
      return null;
    }
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    return null;
  };
  const apply = async (box) => {
    const input = inputOf(box);
    const btn = box.querySelector('[data-a-discount-apply]');
    const code = input && input.value.trim();
    if (!code) { if (input) input.focus(); return; }
    const banner = box.querySelector('cart-discount-banner');
    if (banner) banner.hidden = true;
    if (btn) btn.disabled = true;
    try {
      const before = await (await fetch('/cart.js', { credentials: 'same-origin' })).json();
      const keep = (before.discount_codes || []).filter((d) => d.applicable).map((d) => d.code);
      const res = await fetch('/cart/update.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: keep.concat(code).join(',') })
      });
      const cart = await res.json();
      const hit = (cart.discount_codes || []).find((d) => d.code.toUpperCase() === code.toUpperCase());
      if (!hit || !hit.applicable) {
        // Not valid for this cart: take it off again and say so, keep what the customer typed
        await fetch('/cart/update.js', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: keep.join(',') })
        });
        if (banner) banner.hidden = false;
        return;
      }
      const msg = ((btn && btn.dataset.appliedMsg) || 'הקופון {code} מומש').split('{code}').join(hit.code);
      const fresh = await redraw();
      const target = fresh ? fresh.querySelector('[data-a-discount-ok]') : box.querySelector('[data-a-discount-ok]');
      if (target && msg) { target.textContent = msg; target.hidden = false; }
      if (!fresh && input) input.value = '';
    } catch (e) {
    } finally {
      if (btn && btn.isConnected) btn.disabled = false;
    }
  };
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-a-discount-apply]');
    if (!btn) return;
    e.preventDefault();
    apply(fieldOf(btn));
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' || !e.target.matches || !e.target.matches('cart-discount-field input:not([type="hidden"])')) return;
    e.preventDefault();
    apply(fieldOf(e.target));
  }, true);
})();
